import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AuthContext, MyContext } from '../context/AuthContext';

/**
 * DEATH WARNING MODAL
 *
 * Pops once per session when the current user is at risk of dying at
 * the next monthly wipe — i.e. their wallet won't carry them past
 * 00:00 UTC on the 1st. The modal explains the death model in plain
 * language so users aren't surprised.
 *
 * Suppression: a localStorage key per session prevents nagging. The
 * key is cleared when the session ends (browser close).
 */

const SESSION_KEY = 'wipe_death_warning_seen';

function fmtDur(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h`;
  return `${Math.floor(s / 60)}m`;
}

function DeathWarningModal() {
  const { userRecord } = useContext<MyContext>(AuthContext);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userRecord) return;
    if (userRecord.dying_at) return; // already dying — different UI surface
    if (userRecord.permanence_tokens > 0) return; // permanence tokens save them
    let seen = false;
    try { seen = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { /* ignore */ }
    if (seen) return;

    const now = new Date();
    const wipeMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0);
    const secondsToWipe = Math.max(0, Math.floor((wipeMs - Date.now()) / 1000));
    if (userRecord.time_wallet_seconds <= secondsToWipe) {
      setOpen(true);
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    }
  }, [userRecord]);

  if (!userRecord) return null;

  const now = new Date();
  const wipeMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0);
  const secondsToWipe = Math.max(0, Math.floor((wipeMs - Date.now()) / 1000));
  const walletDuration = fmtDur(userRecord.time_wallet_seconds);
  const wipeDuration = fmtDur(secondsToWipe);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          border: '2px solid currentColor',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: '"VT323", monospace',
          fontSize: '1.5rem',
          letterSpacing: 1,
          textTransform: 'uppercase',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: '#a02018',
          borderBottom: '2px solid currentColor',
        }}
      >
        <WarningAmberIcon sx={{ color: '#a02018' }} />
        Death notice
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ mb: 2, fontFamily: '"VT323", monospace', fontSize: '1.05rem' }}>
          Your wallet will not survive the next wipe.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            rowGap: 0.5,
            columnGap: 2,
            mb: 2,
            fontFamily: '"VT323", monospace',
            fontSize: '0.95rem',
          }}
        >
          <span>Time in your wallet</span>
          <strong>{walletDuration}</strong>
          <span>Time until next wipe</span>
          <strong>{wipeDuration}</strong>
        </Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          On wipe night, every account with zero wallet enters a <strong>7-day purgatory</strong>.
          During purgatory, others can donate time to revive you. After 7 days, if your wallet is
          still empty, your account is permanently deleted along with everything you've made
          (your permanent posts survive under <strong>@wiped</strong>).
        </Typography>
        <Typography variant="body2" color="text.secondary">
          To stay alive: post something, get followed, get upvoted, or visit the store.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={() => setOpen(false)} variant="text">
          Acknowledge
        </Button>
        <Button component={Link} to="/store" variant="outlined" onClick={() => setOpen(false)}>
          Visit store
        </Button>
        <Button component={Link} to="/graveyard" variant="contained" onClick={() => setOpen(false)}>
          See the graveyard
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeathWarningModal;
